import { useSyncExternalStore } from "react";

/**
 * Which business Control Center is acting as, if any (control-center-split.md, section 4).
 *
 * In sessionStorage, so it lasts across reloads of this tab and never leaks into another tab:
 * acting as a business is a deliberate step, taken again in each window. The shared API client
 * reads the id on every request and sends it as `X-Act-As-Business`.
 */

export type ActingAs = { businessId: string; businessName: string };

const KEY = "teamna-cc-acting-as";

function readStored(): ActingAs | null {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<ActingAs>;
    if (typeof value.businessId === "string" && typeof value.businessName === "string") {
      return { businessId: value.businessId, businessName: value.businessName };
    }
  } catch {
    // Storage blocked or a bad value: not acting.
  }
  return null;
}

let current: ActingAs | null = typeof window === "undefined" ? null : readStored();
const listeners = new Set<() => void>();

export function getActingAs(): ActingAs | null {
  return current;
}

export function setActingAs(next: ActingAs | null): void {
  current = next;
  try {
    if (next === null) window.sessionStorage.removeItem(KEY);
    else window.sessionStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Kept in memory only.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useActingAs(): ActingAs | null {
  return useSyncExternalStore(subscribe, getActingAs, getActingAs);
}

/** The headers the shared client sends while acting. Empty when not acting. */
export function actAsHeaders(): Record<string, string> {
  return current ? { "X-Act-As-Business": current.businessId } : {};
}
